import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { 
  LayoutDashboard, 
  Users, 
  Building2,
  BookOpen,
  Calculator,
  Wallet,
  Shield,
  ChevronDown,
  Menu,
  X,
  Sparkles,
  CalendarDays,
  FolderOpen,
  PanelLeftClose,
  PanelLeft
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSidebarContext } from '@/contexts/SidebarContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { endOfDay } from 'date-fns';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  subtitle?: string;
  end?: boolean;
  badge?: string;
  collapsed?: boolean;
}

function NavItem({ to, icon, label, subtitle, end = false, badge, collapsed = false }: NavItemProps) {
  const content = (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-200',
          collapsed ? 'px-3 py-3 justify-center' : 'px-4 py-3',
          isActive
            ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/25'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        )
      }
    >
      {icon}
      {!collapsed && <span className="flex-1">{label}</span>}
      {!collapsed && badge && (
        <span className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary font-semibold">
          {badge}
        </span>
      )}
      {collapsed && badge && (
        <span className="absolute -top-1 -right-1 w-5 h-5 text-xs rounded-full bg-destructive text-destructive-foreground flex items-center justify-center font-semibold">
          {badge}
        </span>
      )}
    </NavLink>
  );

  // Only show tooltip when collapsed (to show label and subtitle)
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative">{content}</div>
        </TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          <div>
            {label}
            {subtitle && <span className="block text-xs text-muted-foreground">{subtitle}</span>}
          </div>
          {badge && <span className="ml-2 text-primary">({badge})</span>}
        </TooltipContent>
      </Tooltip>
    );
  }

  // When not collapsed, render without tooltip wrapper
  return content;
}

export function Sidebar() {
  const { user } = useAuth();
  const location = useLocation();
  const { isCollapsed, toggle } = useSidebarContext();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(
    location.pathname.startsWith('/admin')
  );

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';

  // Fetch pending reminders count for today
  const { data: pendingRemindersCount = 0 } = useQuery({
    queryKey: ['pending-reminders-count'],
    queryFn: async () => {
      const end = endOfDay(new Date());
      const { count, error } = await supabase
        .from('reminders')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending')
        .lte('remind_at', end.toISOString());
      if (error) throw error;
      return count || 0;
    },
    refetchInterval: 60000,
  });

  return (
    <>
      {/* Mobile toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </Button>

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 bg-card border-r border-border flex flex-col transition-all duration-300',
          isCollapsed ? 'w-16' : 'w-64',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* Logo */}
        <div className={cn(
          'h-16 flex items-center border-b border-border',
          isCollapsed ? 'px-3 justify-center' : 'px-6'
        )}>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg shadow-primary/25 flex-shrink-0">
              <Sparkles className="h-5 w-5 text-primary-foreground" />
            </div>
            {!isCollapsed && (
              <div className="flex flex-col">
                <span className="font-bold text-foreground text-lg">CEE Sales</span>
                <span className="text-xs text-muted-foreground">Plateforme commerciale</span>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav 
          data-tour="sidebar-nav"
          className={cn(
            'flex-1 space-y-2 overflow-y-auto',
            isCollapsed ? 'p-2' : 'p-4'
          )}
        >
          <NavItem
            to="/"
            icon={<LayoutDashboard className="h-5 w-5" />}
            label="Dashboard"
            end
            collapsed={isCollapsed}
          />

          <NavItem
            to="/planning"
            icon={<CalendarDays className="h-5 w-5" />}
            label="Planning"
            badge={pendingRemindersCount > 0 ? String(pendingRemindersCount) : undefined}
            collapsed={isCollapsed}
          />
          
          <NavItem
            to="/leads"
            icon={<Users className="h-5 w-5" />}
            label="Leads"
            subtitle="Vos prospects commerciaux"
            collapsed={isCollapsed}
          />

          <NavItem
            to="/dossiers"
            icon={<FolderOpen className="h-5 w-5" />}
            label="Dossiers"
            subtitle="Dossiers CEE en cours"
            collapsed={isCollapsed}
          />
          
          <NavItem
            to="/clients"
            icon={<Building2 className="h-5 w-5" />}
            label="Clients"
            collapsed={isCollapsed}
          />

          {!isCollapsed && (
            <div className="pt-4 pb-2">
              <span className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Outils
              </span>
            </div>
          )}
          
          <NavItem
            to="/aides"
            icon={<BookOpen className="h-5 w-5" />}
            label="Centre de ressources"
            subtitle="Scripts, formations, documents"
            collapsed={isCollapsed}
          />
          
          <NavItem
            to="/calculatrice"
            icon={<Calculator className="h-5 w-5" />}
            label="Calculatrice"
            collapsed={isCollapsed}
          />
          
          <NavItem
            to="/finances"
            icon={<Wallet className="h-5 w-5" />}
            label="Finances"
            collapsed={isCollapsed}
          />

          {/* Admin Section */}
          {isAdmin && (
            <>
              {!isCollapsed && (
                <div className="pt-4 pb-2">
                  <span className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Administration
                  </span>
                </div>
              )}
              
              {isCollapsed ? (
                <NavItem
                  to="/admin"
                  icon={<Shield className="h-5 w-5" />}
                  label="Console Admin"
                  collapsed={isCollapsed}
                />
              ) : (
                <Collapsible open={adminOpen} onOpenChange={setAdminOpen}>
                  <CollapsibleTrigger asChild>
                    <button className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:bg-accent transition-colors">
                      <div className="flex items-center gap-3">
                        <Shield className="h-5 w-5" />
                        <span>Admin</span>
                      </div>
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 transition-transform',
                          adminOpen && 'rotate-180'
                        )}
                      />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-4 space-y-1 mt-1">
                    <NavItem
                      to="/admin"
                      icon={<Shield className="h-4 w-4" />}
                      label="Console Admin"
                      end
                    />
                  </CollapsibleContent>
                </Collapsible>
              )}
            </>
          )}

          {/* Manager Section - Objectifs équipe */}
          {isManager && (
            <>
              {!isCollapsed && (
                <div className="pt-4 pb-2">
                  <span className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Mon équipe
                  </span>
                </div>
              )}
              
              <NavItem
                to="/admin/objectives"
                icon={<Shield className="h-5 w-5" />}
                label="Objectifs équipe"
                collapsed={isCollapsed}
              />
            </>
          )}
        </nav>

        {/* Collapse toggle button */}
        <div className={cn(
          'p-3 border-t border-border',
          isCollapsed ? 'flex justify-center' : ''
        )}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size={isCollapsed ? 'icon' : 'sm'}
                onClick={toggle}
                className={cn(
                  'text-muted-foreground hover:text-foreground',
                  !isCollapsed && 'w-full justify-start gap-2'
                )}
              >
                {isCollapsed ? (
                  <PanelLeft className="h-5 w-5" />
                ) : (
                  <>
                    <PanelLeftClose className="h-5 w-5" />
                    <span>Réduire le menu</span>
                  </>
                )}
              </Button>
            </TooltipTrigger>
            {isCollapsed && (
              <TooltipContent side="right">
                Ouvrir le menu
              </TooltipContent>
            )}
          </Tooltip>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
    </>
  );
}